import { SearchModes, type TransportAirport } from '@/domainLogic/index.ts';
import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import { type Target } from '@/app/components/search/components/Form';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import { TA_RATING_FILTER_TRANSLATION_KEYS } from '@/app/common/reviews/taRatingFilterKeys.ts';
import { hasStarsWording } from '@/app/common/components/Stars/utils.ts';
import getSearchParams from '../../../../common/getSearchParams';
import type { Localization } from '../../../../common/Localization';
import { getFacilitiesDict } from '../../verticalFilters/Fields/getFacilitiesField/getFacilitiesOptions';
import { getBeachTypeOptions } from '../../verticalFilters/Fields/getBeachTypeField';
import { getClimateOptions } from '../../verticalFilters/Fields/getClimateField';
import { getEntertainmentsOptions } from '../../verticalFilters/Fields/getEntertainmentsField';
import { getFaunaOptions } from '../../verticalFilters/Fields/getFaunaField';
import { propertyTypes } from '../../verticalFilters/Fields/getPropertyTypesField/utils';
import { getWaterSportsOptions } from '../../verticalFilters/Fields/getWaterSportsField';
import { getWaterWavesOptions } from '../../verticalFilters/Fields/getWaterWavesField';
import { getMealPlanCopy } from '../../verticalFilters/Fields/getMealPlanField/getMealPlanField';
import { formatPrice } from '../../../../utils/formatting';
import FilterContainer from '../../verticalFilters/FilterContainer';
import { getMaxPrice } from '../../verticalFilters/Fields/getPriceField/priceUtils.ts';
import type { DynamicFiltersResponse } from '../../../../utils/api/dynamicFiltersResponseTypes';
import {
  getLocalStorageObject,
  LOCAL_STORAGE_KEY,
  removeLocalStorage,
  setLocalStorageObject,
} from '../../../../utils/localStorage';

export const getValue = (values: string[], firstSelection: string) => {
  if (values.length > 1) {
    return `${firstSelection} +${values.length - 1}`;
  }
  return firstSelection;
};

export const checkIsConsecutive = (array: number[]): boolean => {
  if (array.length === 0) {
    return false;
  }

  return array.reduce(
    (isConsecutive, current, index) => !(!isConsecutive || (index > 0 && array[index - 1] + 1 !== current)),
    true
  );
};

export const getMaxPricePlaceholder = ({
  values,
  maxPriceSlider,
  maxPrice,
  t,
  dynamicFiltersResponse,
}: {
  values: string[];
  maxPriceSlider: number;
  maxPrice: string;
  t: TranslationFunction;
  dynamicFiltersResponse?: DynamicFiltersResponse;
}) => {
  const numericMaxPrice = typeof maxPrice === 'string' ? maxPrice.replace(/[^\d.]/g, '') : maxPrice;
  if (Math.floor(parseInt(values[1], 10)) === maxPriceSlider && !dynamicFiltersResponse) {
    return t('vacation.oss.components.searchfilters.labels.nolimit');
  }

  if (Number.isNaN(parseFloat(numericMaxPrice))) {
    return t('vacation.oss.components.searchfilters.labels.nolimit');
  }

  if (Math.floor(parseInt(values[1], 10)) === maxPriceSlider) {
    return `${maxPrice}+`;
  }
  return maxPrice;
};

type GetPriceLabel = {
  searchParams: SearchParams;
  localization: Localization;
  t: TranslationFunction;
  dynamicFiltersResponse?: DynamicFiltersResponse;
};
export const getPriceLabel = ({ searchParams, localization, t, dynamicFiltersResponse }: GetPriceLabel) => {
  const { lang, currency } = localization;
  const values = searchParams[URLParameters.PRICE].split(',');
  const maxPrice = formatPrice({
    price: Math.floor(parseInt(values[1], 10)),
    localization,
  });
  const maxPriceSlider = getMaxPrice(lang, currency);
  const maxPricePlaceholder = getMaxPricePlaceholder({
    values,
    maxPriceSlider,
    maxPrice,
    t,
    dynamicFiltersResponse,
  });
  if (parseInt(values[0], 10) > 0) {
    const minPrice = formatPrice({
      price: Math.floor(parseInt(values[0], 10)),
      localization,
    });
    return `${minPrice} - ${maxPricePlaceholder}`;
  }
  return t('vacation.oss.filters.budget.up_to', { price: maxPricePlaceholder });
};

const getFacilitiesValues = (searchParams: SearchParams) => [
  ...(searchParams[URLParameters.GENERAL_ACCOMODATION_FACILITIES]
    ? searchParams[URLParameters.GENERAL_ACCOMODATION_FACILITIES].split(',')
    : []),
  ...(searchParams[URLParameters.FAMILY_ACCOMODATION_FACILITIES]
    ? searchParams[URLParameters.FAMILY_ACCOMODATION_FACILITIES].split(',')
    : []),
  ...(searchParams[URLParameters.ACTIVITY_ACCOMODATION_FACILITIES]
    ? searchParams[URLParameters.ACTIVITY_ACCOMODATION_FACILITIES].split(',')
    : []),
  ...(searchParams[URLParameters.WELLNESS_ACCOMODATION_FACILITIES]
    ? searchParams[URLParameters.WELLNESS_ACCOMODATION_FACILITIES].split(',')
    : []),
  ...(searchParams[URLParameters.FOOD_ACCOMODATION_FACILITIES]
    ? searchParams[URLParameters.FOOD_ACCOMODATION_FACILITIES].split(',')
    : []),
];

export const getLabel = ({
  control,
  searchParams,
  localization,
  productsData,
  dynamicFiltersResponse,
  t,
  isTripAdvisor = false,
}: {
  control?: string;
  searchParams: SearchParams;
  localization: Localization;
  productsData: ProductsData;
  dynamicFiltersResponse?: DynamicFiltersResponse;
  t: TranslationFunction;
  /** Page-level: any TripAdvisor hotel in the result list switches the rating wording to the TA taxonomy. */
  isTripAdvisor?: boolean;
}): string => {
  const searchParamsFresh = canUseDOM ? getSearchParams() : searchParams;

  switch (control) {
    case FilterContainer.PriceContainer: {
      return getPriceLabel({ searchParams: searchParamsFresh, localization, t, dynamicFiltersResponse });
    }
    case FilterContainer.DestinationAirportContainer: {
      const value = searchParamsFresh[URLParameters.PRE_FILTER_DESTINATION_AIRPORT];
      const airports = productsData?.transport?.airports
        ? productsData.transport.airports
        : getLocalStorageObject(LOCAL_STORAGE_KEY.PREFILTER_DESTINATION_AIRPORT);

      if (airports) {
        const matchedAirport = airports.find((x: TransportAirport) => x.iata === value);

        if (matchedAirport) {
          const { name, iata, cityName } = matchedAirport;
          return `${cityName} (${iata}) ${name}`;
        }
      }
      return '';
    }
    case FilterContainer.OvernightFlightsContainer: {
      return searchParamsFresh[URLParameters.SEARCH_MODE] === SearchModes.DP
        ? t('vacation.oss.overnight_flight.filter.label')
        : '';
    }
    case FilterContainer.RatingContainer: {
      if (!searchParamsFresh[URLParameters.RATING]) return '';
      const values = searchParamsFresh[URLParameters.RATING].split(',');
      const taKey = isTripAdvisor ? TA_RATING_FILTER_TRANSLATION_KEYS[values[0]] : undefined;
      const firstSelection = t(taKey ?? `vacation.oss.filters.rating.${values[0] === 'pleasant' ? 'fair' : values[0]}`);
      return getValue(values, firstSelection);
    }
    case FilterContainer.FacilitiesContainer: {
      const values = getFacilitiesValues(searchParamsFresh);
      const firstSelection = getFacilitiesDict(t).filter((x) => x.id === parseInt(values[0], 10))?.[0]?.text || '';
      return getValue(values, firstSelection);
    }
    case FilterContainer.MealContainer: {
      if (!searchParamsFresh[URLParameters.MEAL]) return '';
      const values = searchParamsFresh[URLParameters.MEAL].split(',');
      const firstSelection = getMealPlanCopy(t)[parseInt(values[0], 10)];
      return getValue(values, firstSelection);
    }
    case FilterContainer.StarsContainer: {
      if (!searchParamsFresh[URLParameters.STARS]) return '';
      const values = searchParamsFresh[URLParameters.STARS]
        .split(',')
        .map((value) => parseInt(value, 10))
        .sort((a, b) => a - b);
      const number =
        values.length > 1 && checkIsConsecutive(values)
          ? `${values[0]} - ${values[values.length - 1]}`
          : values.join(' - ');
      return hasStarsWording(localization.lang) ? t('vacation.oss.filters.stars.placeholder', { number }) : number;
    }
    case FilterContainer.PropertyTypeContainer: {
      if (!searchParamsFresh[URLParameters.ACCOMODATION_TYPE]) return '';
      const values = searchParamsFresh[URLParameters.ACCOMODATION_TYPE].split(',');
      const firstSelection = propertyTypes(t).filter((x) => x.id === parseInt(values[0], 10))[0].text;
      return getValue(values, firstSelection);
    }
    case FilterContainer.SortContainer: {
      return t('vacation.oss.components.searchresults.sort.title') !== ''
        ? t('vacation.oss.components.searchresults.sort.title')
        : t('vacation.oss.components.searchresults.sort.sortby');
    }
    case FilterContainer.BeachTypeContainer: {
      if (!searchParamsFresh[URLParameters.BEACH_TYPE]) return '';
      const values = searchParamsFresh[URLParameters.BEACH_TYPE].split(',');
      const firstSelection = getBeachTypeOptions({ t }).filter((x) => x.value === parseInt(values[0], 10))[0].label;
      return getValue(values, firstSelection);
    }
    case FilterContainer.ClimateContainer: {
      if (!searchParamsFresh[URLParameters.CLIMATE]) return '';
      const values = searchParamsFresh[URLParameters.CLIMATE].split(',');
      const firstSelection = getClimateOptions({ t }).filter((x) => x.value === parseInt(values[0], 10))[0].label;
      return getValue(values, firstSelection);
    }
    case FilterContainer.FaunaContainer: {
      if (!searchParamsFresh[URLParameters.FAUNA]) return '';
      const values = searchParamsFresh[URLParameters.FAUNA].split(',');
      const firstSelection = getFaunaOptions({ t }).filter((x) => x.value === parseInt(values[0], 10))[0].label;
      return getValue(values, firstSelection);
    }
    case FilterContainer.WatersportContainer: {
      if (!searchParamsFresh[URLParameters.WATERSPORT]) return '';
      const values = searchParamsFresh[URLParameters.WATERSPORT].split(',');
      const firstSelection = getWaterSportsOptions({ t }).filter((x) => x.value === parseInt(values[0], 10))[0].label;
      return getValue(values, firstSelection);
    }
    case FilterContainer.EntertainmentsContainer: {
      if (!searchParamsFresh[URLParameters.ENTERTAINMENTS]) return '';
      const values = searchParamsFresh[URLParameters.ENTERTAINMENTS].split(',');
      const firstSelection = getEntertainmentsOptions({ t }).filter((x) => x.value === parseInt(values[0], 10))[0]
        .label;
      return getValue(values, firstSelection);
    }
    case FilterContainer.WaterwavesContainer: {
      if (!searchParamsFresh[URLParameters.WATER_WAVES]) return '';
      const values = searchParamsFresh[URLParameters.WATER_WAVES].split(',');
      const firstSelection = getWaterWavesOptions({ t }).filter((x) => x.value === parseInt(values[0], 10))[0].label;
      return getValue(values, firstSelection);
    }
    default:
      return '';
  }
};

export const setPosition = (
  containerRef: React.MutableRefObject<HTMLButtonElement | undefined>,
  overlayRef: React.MutableRefObject<HTMLDivElement | undefined>,
  width?: number
) => {
  if (!containerRef.current || !overlayRef.current) return;
  const marginRight = 8;
  const { left } = containerRef.current.getBoundingClientRect();
  const right = window.innerWidth - containerRef.current.getBoundingClientRect().right - marginRight;
  const formWidth = (overlayRef.current.childNodes[0] as HTMLDivElement).getBoundingClientRect().width;
  const widthStyle = width ? `width: ${width}px;` : `width: ${formWidth + 32}px;`;

  overlayRef.current.setAttribute(
    'style',
    `
      ${widthStyle}
      left: ${left}px;
      right: ${right < 0 ? marginRight : right}px;
    `
  );
};

export const addAirportsToLocalStorage = ({ e, productsData }: { e: Target; productsData: ProductsData }) => {
  if (e.target.name === 'prefilter_destination_airport') {
    if (e.target.value && e.target.value !== '0') {
      setLocalStorageObject(LOCAL_STORAGE_KEY.PREFILTER_DESTINATION_AIRPORT, productsData?.transport?.airports);
    } else {
      removeLocalStorage(LOCAL_STORAGE_KEY.PREFILTER_DESTINATION_AIRPORT);
    }
  }
};

export const getLoaderSizeFromLabel = (label: string): string => {
  const characterWidth = 9;
  return `${label.length * characterWidth}px`;
};
